import { MotionDiv } from '@/components/element/MotionDiv';
import { useUpdatePurchaseOrderContractPaymentScheduleChecked } from '@/hooks/api/0821_update_payment_schedule_checked';
import { useUpdatePurchaseOrderContractLegalChecked } from '@/hooks/api/0822_update_legal_checked';
import { useUpdateNoneDocuments } from '@/hooks/api/0822_update_none_documents';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPurchaseOrderContractTask } from '@/providers/PurchaseOrderContractTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUpdateRelatedOrderChecked } from '@/hooks/api/0825_update_related_order_checked';

export const ToDoList = () => {
  const { meTeamMember } = useCurrentTeamMember();
  const { purchaseOrderContractTask: task } = useCurrentPurchaseOrderContractTask();
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    if (task) {
      setDisabled(task!.purchase_order_contract_task_phase.name != 'preparing');
    }
  }, [task]);

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header">
        <h6 className="mb-1">TODOリスト</h6>
        <p className="text-xs mb-0">TODOを処理して、タスクを次のフェーズへ移動させてください。</p>
      </div>
      <div className="card-body pt-0">
        <ul className="list-group list-group-flush" data-toggle="checklist">
          <UpdateNoneDocumentElement disabled={disabled} />
          {task!.contract_context.name != 'nda' && <PaymentScheduleCheckElement disabled={disabled} />}
          <LegalCheckElement disabled={disabled} />
          {task!.contract_context.name != 'nda' && <RelatedOrderCheckElement disabled={disabled} />}
        </ul>
      </div>
    </MotionDiv>
  );
};

const UpdateNoneDocumentElement = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task, setPurchaseOrderContractTask } = useCurrentPurchaseOrderContractTask();
  const { mutate, data, isPending, error } = useUpdateNoneDocuments();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onNoneDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id!),
      id: task!.id,
      none_documents: e.target.checked,
    });
  };

  useEffect(() => {
    if (data) setPurchaseOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.has_document || task!.none_documents ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            契約関連書類の添付
            {task!.has_document || task!.none_documents ? (
              <></>
            ) : (
              <i className="fas fa-exclamation-circle ms-1 text-danger"></i>
            )}
          </h6>
          <div className="text-weak text-xs mt-1">承認依頼対象の契約関連書類を添付してください。</div>
        </div>
        {!task!.has_document && (
          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="noneDocumentsCheck"
              checked={task!.none_documents}
              onChange={onNoneDocumentsChange}
              disabled={disabled}
            />
            <label className="custom-control-label" htmlFor="noneDocumentsCheck">
              関連書類なし
            </label>
          </div>
        )}
      </div>
      <hr className="horizontal dark mt-4 mb-0" />
    </li>
  );
};

const PaymentScheduleCheckElement = ({ disabled }: { disabled: boolean }) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task, setPurchaseOrderContractTask } = useCurrentPurchaseOrderContractTask();
  const { mutate, data, isPending, error } = useUpdatePurchaseOrderContractPaymentScheduleChecked();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onPaymentScheduleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id!),
      id: task!.id,
      payment_schedule_checked: e.target.checked,
    });
  };

  useEffect(() => {
    if (data) setPurchaseOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.payment_schedule_checked ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            プロジェクト計画金額や期間の最新化
            {task!.payment_schedule_checked ? <></> : <i className="fas fa-exclamation-circle ms-1 text-danger"></i>}
          </h6>
          <div className="text-weak text-xs mt-1">
            {!disabled && (
              <>
                プロジェクトの計画金額や期間が変更になっている場合、
                <Link
                  to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/edit`}
                  state={{ prevPath: location.pathname }}
                  style={{ textDecoration: 'underline' }}
                >
                  こちら
                </Link>
                から変更してください。
              </>
            )}
          </div>
        </div>
        <div className="form-group mt-3">
          <label className="mx-0">プロジェクト計画金額（税抜）</label>
          <div className="form-control-plaintext">¥{task!.purchase_order.accounting_total_amount.toLocaleString()}</div>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="billingScheduleCheck"
            checked={task!.payment_schedule_checked}
            onChange={onPaymentScheduleChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor="billingScheduleCheck">
            プロジェクト計画金額や期間の最新化実施済
          </label>
        </div>
      </div>
    </li>
  );
};

const LegalCheckElement = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task, setPurchaseOrderContractTask } = useCurrentPurchaseOrderContractTask();
  const { mutate, data, isPending, error } = useUpdatePurchaseOrderContractLegalChecked();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onLegalCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id!),
      id: task!.id,
      legal_checked: e.target.checked,
    });
  };

  useEffect(() => {
    if (data) setPurchaseOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.legal_checked ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            法務確認
            {task!.legal_checked ? <></> : <i className="fas fa-exclamation-circle ms-1 text-danger"></i>}
          </h6>
          <div className="text-weak text-xs mt-1">
            チーム内のルールに従い対応が必要な場合は、法務確認を行なってください。
          </div>
        </div>
        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="legalCheck"
            checked={task!.legal_checked}
            onChange={onLegalCheckChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor="legalCheck">
            法務確認不要、または法務確認の上問題ないことを確認
          </label>
        </div>
      </div>
    </li>
  );
};

const RelatedOrderCheckElement = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task, setPurchaseOrderContractTask } = useCurrentPurchaseOrderContractTask();
  const { mutate, data, isPending, error } = useUpdateRelatedOrderChecked();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onRelatedOrderCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id!),
      id: task!.id,
      related_order_checked: e.target.checked,
    });
  };

  useEffect(() => {
    if (data) setPurchaseOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.related_order_checked ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            関連プロジェクト（売上）の最新化
            {task!.related_order_checked ? <></> : <i className="fas fa-exclamation-circle ms-1 text-danger"></i>}
          </h6>
          <div className="text-weak text-xs mt-1">
            {!disabled && (
              <>
                関連する受注プロジェクトを
                <Link
                  to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${purchase_order_id}`}
                  style={{ textDecoration: 'underline' }}
                >
                  こちら
                </Link>
                から設定してください。
              </>
            )}
          </div>
        </div>
        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="relatedOrderCheck"
            checked={task!.related_order_checked}
            onChange={onRelatedOrderCheckChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor="relatedOrderCheck">
            関連する受注プロジェクトの最新化または関連受注プロジェクトがないことを確認済
          </label>
        </div>
      </div>
    </li>
  );
};
